/** ITU-T G.711 A-law encode of a 16-bit PCM sample in [-1, 1] float. */
const ALAW_CLIP = 32635;
const SEG_END = [0xff, 0x1ff, 0x3ff, 0x7ff, 0xfff, 0x1fff, 0x3fff, 0x7fff];
export const G711_RATE = 8000;
export const G711_FRAME_SAMPLES = 160;

function searchSegment(val: number): number {
  for (let i = 0; i < 8; i++) {
    if (val <= SEG_END[i]!) return i;
  }
  return 7;
}

function hermite(y0: number, y1: number, y2: number, y3: number, t: number): number {
  const c1 = 0.5 * (y2 - y0);
  const c2 = y0 - 2.5 * y1 + 2 * y2 - 0.5 * y3;
  const c3 = 0.5 * (y3 - y0) + 1.5 * (y1 - y2);
  return ((c3 * t + c2) * t + c1) * t + y1;
}

/** 2nd-order Butterworth low-pass (RBJ). */
class BiquadLowpass {
  private readonly b0: number;
  private readonly b1: number;
  private readonly b2: number;
  private readonly a1: number;
  private readonly a2: number;
  private z1 = 0;
  private z2 = 0;

  constructor(cutoffHz: number, fs: number) {
    const w0 = (2 * Math.PI * cutoffHz) / fs;
    const cos = Math.cos(w0);
    const alpha = Math.sin(w0) / (2 * Math.SQRT1_2);
    const a0 = 1 + alpha;
    this.b0 = (1 - cos) / 2 / a0;
    this.b1 = (1 - cos) / a0;
    this.b2 = (1 - cos) / 2 / a0;
    this.a1 = (-2 * cos) / a0;
    this.a2 = (1 - alpha) / a0;
  }

  step(x: number): number {
    const y = this.b0 * x + this.z1;
    this.z1 = this.b1 * x - this.a1 * y + this.z2;
    this.z2 = this.b2 * x - this.a2 * y;
    return y;
  }
}

/**
 * Stream resampler to 8 kHz. Keeps fractional phase across chunks so samples
 * are never dropped or duplicated at AudioWorklet boundaries.
 */
export class PcmTo8k {
  private inRate: number;
  private lp1: BiquadLowpass | null = null;
  private lp2: BiquadLowpass | null = null;
  private buf: number[] = [0, 0, 0, 0];
  private frac = 1;

  constructor(inputRate: number) {
    this.inRate = Math.max(1, inputRate || G711_RATE);
    this.configureFilter();
  }

  get inputRate(): number {
    return this.inRate;
  }

  setInputRate(inputRate: number): void {
    const next = Math.max(1, inputRate || G711_RATE);
    if (next === this.inRate) return;
    this.inRate = next;
    this.buf = [0, 0, 0, 0];
    this.frac = 1;
    this.configureFilter();
  }

  private configureFilter(): void {
    if (this.inRate <= G711_RATE) {
      this.lp1 = null;
      this.lp2 = null;
      return;
    }
    const cutoff = Math.min(3400, this.inRate * 0.45);
    this.lp1 = new BiquadLowpass(cutoff, this.inRate);
    this.lp2 = new BiquadLowpass(cutoff, this.inRate);
  }

  process(input: Float32Array): Float32Array {
    if (input.length === 0) return input;
    if (this.inRate === G711_RATE) return input;

    const ratio = this.inRate / G711_RATE;
    for (let i = 0; i < input.length; i++) {
      let s = input[i]!;
      if (this.lp1) s = this.lp1.step(s);
      if (this.lp2) s = this.lp2.step(s);
      this.buf.push(s);
    }

    const out: number[] = [];
    while (this.frac + 2 < this.buf.length) {
      const i1 = Math.floor(this.frac);
      const t = this.frac - i1;
      const y0 = this.buf[i1 - 1] ?? this.buf[i1]!;
      const y1 = this.buf[i1]!;
      const y2 = this.buf[i1 + 1]!;
      const y3 = this.buf[i1 + 2]!;
      out.push(hermite(y0, y1, y2, y3, t));
      this.frac += ratio;
    }

    const drop = Math.max(0, Math.floor(this.frac) - 1);
    if (drop > 0) {
      if (drop >= this.buf.length) {
        this.frac -= this.buf.length;
        this.buf = [];
      } else {
        this.buf.splice(0, drop);
        this.frac -= drop;
      }
    }
    return Float32Array.from(out);
  }
}

/** One-shot downsample of a complete buffer (not for chunked capture). */
export function downsampleTo8k(input: Float32Array, inputRate: number): Float32Array {
  if (inputRate === G711_RATE) return input;
  return new PcmTo8k(inputRate).process(input);
}

/**
 * DC block only at 8 kHz. Makeup AGC pumped and made talk-down sound robotic.
 */
export class VoiceConditioner {
  private dcX = 0;
  private dcY = 0;

  process(frame: Float32Array): void {
    const r = 0.939; // ~80 Hz high-pass at 8 kHz
    for (let i = 0; i < frame.length; i++) {
      const x = frame[i]!;
      const y = x - this.dcX + r * this.dcY;
      this.dcX = x;
      this.dcY = y;
      frame[i] = y > 0.95 ? 0.95 : y < -0.95 ? -0.95 : y;
    }
  }
}

export function linearToAlaw(sample: number): number {
  let pcm = Math.round(Math.max(-1, Math.min(1, sample)) * 32767);
  pcm = pcm > 0 ? Math.min(pcm, ALAW_CLIP) : -Math.min(-pcm, ALAW_CLIP);
  let mask: number;
  let aval: number;
  if (pcm >= 0) {
    mask = 0xd5;
  } else {
    mask = 0x55;
    pcm = -pcm - 8;
  }
  const seg = searchSegment(pcm);
  if (seg >= 8) aval = 0x7f;
  else {
    aval = (seg << 4) | ((pcm >> (seg < 2 ? 4 : seg + 3)) & 0x0f);
  }
  return aval ^ mask;
}

export function encodeAlawFrame(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = linearToAlaw(samples[i]!);
  return out;
}

/** ITU-T G.711 A-law expand, normalized to [-1, 1]. */
export function alawToLinear(byte: number): number {
  let aVal = (byte & 0xff) ^ 0x55;
  let t = ((aVal & 0x0f) << 4) + 8;
  const seg = (aVal & 0x70) >> 4;
  if (seg >= 1) t += 0x100;
  if (seg > 1) t <<= seg - 1;
  return ((aVal & 0x80) !== 0 ? t : -t) / 32768;
}

/** ITU-T G.711 μ-law expand, normalized to [-1, 1]. */
export function ulawToLinear(byte: number): number {
  const uVal = ~byte & 0xff;
  let t = ((uVal & 0x0f) << 3) + 0x84;
  t <<= (uVal & 0x70) >> 4;
  const pcm = (uVal & 0x80) !== 0 ? 0x84 - t : t - 0x84;
  return pcm / 32768;
}

export function decodeAlawFrame(bytes: Uint8Array | ArrayBuffer): Float32Array {
  const src = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) out[i] = alawToLinear(src[i]!);
  return out;
}

export function decodeUlawFrame(bytes: Uint8Array | ArrayBuffer): Float32Array {
  const src = bytes instanceof ArrayBuffer ? new Uint8Array(bytes) : bytes;
  const out = new Float32Array(src.length);
  for (let i = 0; i < src.length; i++) out[i] = ulawToLinear(src[i]!);
  return out;
}

const ULAW_BIAS = 0x84;
const ULAW_SEG_END = [0xff, 0x1ff, 0x3ff, 0x7ff, 0xfff, 0x1fff, 0x3fff, 0x7fff];

export function linearToUlaw(sample: number): number {
  let pcm = Math.round(Math.max(-1, Math.min(1, sample)) * 32767);
  let mask: number;
  if (pcm < 0) {
    pcm = ULAW_BIAS - pcm;
    mask = 0x7f;
  } else {
    pcm += ULAW_BIAS;
    mask = 0xff;
  }
  if (pcm > 0x7fff) pcm = 0x7fff;
  let seg = 7;
  for (let i = 0; i < 8; i++) {
    if (pcm <= ULAW_SEG_END[i]!) {
      seg = i;
      break;
    }
  }
  const uval = (seg << 4) | ((pcm >> (seg + 3)) & 0x0f);
  return (uval ^ mask) & 0xff;
}

export function encodeUlawFrame(samples: Float32Array): Uint8Array {
  const out = new Uint8Array(samples.length);
  for (let i = 0; i < samples.length; i++) out[i] = linearToUlaw(samples[i]!);
  return out;
}

export function decodeG711Frame(bytes: Uint8Array, codec: "pcma" | "pcmu"): Float32Array {
  return codec === "pcmu" ? decodeUlawFrame(bytes) : decodeAlawFrame(bytes);
}

/** Stream upsample from 8 kHz to an AudioContext rate. Identity when rates match. */
export class PcmFrom8k {
  private outRate: number;
  private buf: number[] = [0, 0, 0, 0];
  private frac = 1;

  constructor(outputRate: number) {
    this.outRate = Math.max(G711_RATE, outputRate || G711_RATE);
  }

  setOutputRate(outputRate: number): void {
    const next = Math.max(G711_RATE, outputRate || G711_RATE);
    if (next === this.outRate) return;
    this.outRate = next;
    this.buf = [0, 0, 0, 0];
    this.frac = 1;
  }

  process(input: Float32Array): Float32Array {
    if (input.length === 0) return input;
    if (this.outRate === G711_RATE) return input;
    const ratio = G711_RATE / this.outRate;
    for (let i = 0; i < input.length; i++) this.buf.push(input[i]!);
    const out: number[] = [];
    while (this.frac + 2 < this.buf.length) {
      const i1 = Math.floor(this.frac);
      const t = this.frac - i1;
      const y0 = this.buf[i1 - 1] ?? this.buf[i1]!;
      const y1 = this.buf[i1]!;
      const y2 = this.buf[i1 + 1]!;
      const y3 = this.buf[i1 + 2]!;
      out.push(hermite(y0, y1, y2, y3, t));
      this.frac += ratio;
    }
    const drop = Math.max(0, Math.floor(this.frac) - 1);
    if (drop > 0) {
      if (drop >= this.buf.length) {
        this.frac -= this.buf.length;
        this.buf = [];
      } else {
        this.buf.splice(0, drop);
        this.frac -= drop;
      }
    }
    return Float32Array.from(out);
  }
}
